/*
 * Support intake — the "always an out" seam (Duncan rulings
 * BOARDROOM-2026-08-29-120259 #2 + ADDENDUM 122136 + ADDENDUM 2 123126).
 *
 * GROUND-TRUTH OF THE EXISTING MECHANISM (verified 2026-08-29, this branch):
 * the app chrome's "Help and contact support" surface is the topbar help
 * button (Shell.tsx `topbar-help`, tooltip `topbar.help.tip`) opening
 * HelpPanel — whose "Message support" CTA is explicitly INERT (aria-disabled,
 * TBD-IN-DC canon §3.3 / §12.7: "production wires real support + organiser-
 * contact channels"). services/my-api/src/app.ts carries NO support-intake
 * route, and no support address exists in any env file. There is therefore NO
 * wired mailto/form/route to reuse — this module IS the production wiring the
 * canon deferred, kept deliberately minimal:
 *
 *  - CHANNEL: `mailto:` to VITE_SUPPORT_EMAIL. The address is CONFIG, not
 *    code — per PROCESS-3 / PROGRAMME-AD-NO-FABRICATED-DATA-IN-BUILDS-V1 no
 *    plausible-looking address is invented here; unset ⇒ callers render the
 *    honest not-wired state (the myApiBase convention). Both the eligibility-
 *    mismatch out AND HelpPanel's "Message support" CTA route through here,
 *    so one env value lights up every support entry point at once.
 *
 *  - REPORTED-STATE PERSISTENCE: DEVICE-LOCAL (localStorage). my-api has no
 *    support-ticket store to write to (route table verified as above), so a
 *    server-side "open request" record CANNOT exist yet and is not faked: the
 *    record below only asserts what actually happened on this device — a
 *    mailto NAVIGATION WAS ATTEMPTED for this ticket at this time. That is the
 *    LIMIT of what is knowable from JS: assigning a mailto to location.href
 *    yields no success signal, so a browser with NO registered mail handler
 *    silently no-ops, and a compose window opened-then-abandoned never sends —
 *    both are UNDETECTABLE here (P1-1 residual). Callers must therefore keep a
 *    re-open affordance in the reported state (the out is never a dead end on
 *    a handlerless device). The "reference" shown to the participant is the
 *    ticket id + report date (real facts support can key on), NEVER a
 *    fabricated case number. When a server intake seam lands, this store
 *    migrates behind the same functions.
 */

export type MismatchReason = 'gender' | 'age' | null

export interface SupportPrefill {
  /** The held ticket the mismatch is about. */
  ticketId?: string
  eventName?: string
  /** Participant display name (from their own profile draft — never invented). */
  participantName?: string
  /** Account email off the verified session claims. */
  participantEmail?: string
  /** The locale-resolved mismatch copy shown to the participant. */
  reason?: string
  /** Machine axis (gender|age) so support can triage without parsing prose. */
  reasonCode?: MismatchReason
}

/** The configured support address, or null (honest absence — never guessed). */
export function supportEmail(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_SUPPORT_EMAIL || null
}

/**
 * Pre-filled mailto href — ticket id + participant + mismatch reason ride the
 * subject/body so support needs no interrogation (ruling 2). Returns null when
 * no support address is configured (callers show the honest not-wired state).
 */
export function supportMailtoHref(
  prefill: SupportPrefill = {},
  env: Record<string, string | undefined> = import.meta.env,
): string | null {
  const to = supportEmail(env)
  if (!to) return null
  const subject = prefill.ticketId
    ? `Support request — ticket ${prefill.ticketId}`
    : 'Support request — My Trumin'
  const lines: string[] = []
  if (prefill.ticketId) lines.push(`Ticket: ${prefill.ticketId}`)
  if (prefill.eventName) lines.push(`Event: ${prefill.eventName}`)
  if (prefill.participantName) lines.push(`Participant: ${prefill.participantName}`)
  if (prefill.participantEmail) lines.push(`Account email: ${prefill.participantEmail}`)
  if (prefill.reasonCode) lines.push(`Mismatch axis: ${prefill.reasonCode}`)
  if (prefill.reason) lines.push(`Reason: ${prefill.reason}`)
  lines.push('', 'Please help me resolve this.')
  const q = new URLSearchParams({ subject, body: lines.join('\n') })
  // URLSearchParams encodes spaces as '+', which mail clients render literally
  // in mailto payloads — normalise to %20.
  return `mailto:${to}?${q.toString().replace(/\+/g, '%20')}`
}

/**
 * Attempt to open the pre-filled support email. Returns true when a configured
 * mailto navigation was ATTEMPTED, false when unconfigured (nothing happened —
 * callers surface the honest not-wired state and MUST NOT mark anything as
 * reported).
 *
 * HONESTY LIMIT (P1-1): true does NOT mean a compose window opened, and never
 * can — location.href mailto assignment returns no signal, mail-handler
 * absence silently no-ops, and compose-without-send is invisible to JS. The
 * reported state derived from this is "attempted", and the notice keeps a
 * re-open link so the out survives a silent no-op.
 */
export function openSupportMailto(
  prefill: SupportPrefill = {},
  opener: (href: string) => void = (href) => { window.location.href = href },
  env: Record<string, string | undefined> = import.meta.env,
): boolean {
  const href = supportMailtoHref(prefill, env)
  if (!href) return false
  opener(href)
  return true
}

// ---------------------------------------------------------------------------
// Reported-state store (ADDENDUM sub-state 2: banner+button → "support request
// submitted", no re-nag). DEVICE-LOCAL by necessity — see the header note.
// Keyed PER TICKET so a support-side ticket swap starts clean on the new
// ticket (completion/report state never couples to the mismatched ticket).
// ---------------------------------------------------------------------------

const REPORTS_KEY = 'my.eligibilitySupportReports.v1'

export interface EligibilityReport {
  /** Machine axis reported (gender|age|null when the server sent none). */
  reason: MismatchReason
  /** ISO timestamp of the report — the participant-visible reference date. */
  at: string
}

function readReports(): Record<string, EligibilityReport> {
  try {
    const raw = localStorage.getItem(REPORTS_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, EligibilityReport>) : {}
  } catch {
    return {}
  }
}

function writeReports(reports: Record<string, EligibilityReport>): void {
  try {
    localStorage.setItem(REPORTS_KEY, JSON.stringify(reports))
  } catch {
    // Persistence unavailable (private mode etc.) — the in-session state the
    // caller holds still applies; nothing is silently invented.
  }
}

/** The recorded report for a ticket, or null (never fabricated). */
export function eligibilityReport(ticketId: string): EligibilityReport | null {
  return readReports()[ticketId] ?? null
}

/** Record that a support-email mailto navigation was ATTEMPTED for this
 *  ticket (see the honesty-limit note on openSupportMailto — "opened" and
 *  "sent" are unknowable from JS; the record claims neither). */
export function markEligibilityReported(
  ticketId: string,
  reason: MismatchReason,
  now: () => Date = () => new Date(),
): EligibilityReport {
  const report: EligibilityReport = { reason, at: now().toISOString() }
  const reports = readReports()
  reports[ticketId] = report
  writeReports(reports)
  return report
}

/** Test hygiene / future server-migration hook. */
export function clearEligibilityReports(): void {
  try {
    localStorage.removeItem(REPORTS_KEY)
  } catch {
    // nothing to clear
  }
}
